import FontMapData from './assets/font-map.json';
import { Sprite } from './engine/assetLoader';
import Vector2d from './engine/vector2d';
import { TILE_SIZE } from './maze';

export enum Color {
  White,
  Yellow,
  Cyan,
  Purple,
  Red,
  Blue,
  Green,
}

export enum FontSize {
  Small,
  Normal,
  Large
}

type FontMap = {
  [key: string]: {
    x: number;
    y: number;
  }
};

const BITMAP_WIDTH = 25;
const BITMAP_HEIGHT = 25;

const getChar = (char: string) => {
  const fontMap = FontMapData as unknown as FontMap;

  return fontMap[char.toLocaleUpperCase()] ?? {
    x: 25,
    y: 1
  };
};

export default class TextRenderer {
  private bufferCanvas: HTMLCanvasElement;
  private bufferCtx: CanvasRenderingContext2D;

  constructor(protected sprite: Sprite | undefined) {
    this.bufferCanvas = document.createElement('canvas');
    this.bufferCtx = this.bufferCanvas.getContext('2d')!;
  }

  private getDimensions(size: FontSize) {
    switch (size) {
      case FontSize.Large:
        return {
          width: 24,
          height: 24,
        };
      case FontSize.Normal:
        return {
          width: 16,
          height: 16,
        };
      case FontSize.Small:
        return {
          width: 8,
          height: 8,
        };
    }
  }

  private renderCharacter(char: string, size: FontSize, index: number, ctx: CanvasRenderingContext2D): void {
    const { sprite } = this;
    const { width, height } = this.getDimensions(size);
    const { x, y } = getChar(char);

    if (!sprite) {
      return;
    }

    ctx.drawImage(
      sprite.sprite,
      BITMAP_WIDTH * x,
      BITMAP_HEIGHT * y,
      BITMAP_WIDTH,
      BITMAP_HEIGHT,
      index * width,
      0,
      width,
      width,
    );
  }

  private getColor(color: Color): string {
    switch (color) {
      case Color.Cyan:
        return '#00ffff';
      case Color.Yellow:
        return '#ffff00';
      case Color.Red:
        return '#ff0000';
      case Color.Purple:
        return '#ff00ff';
      case Color.Blue:
        return '#0000ff';
      case Color.Green:
        return '#00ff00';
      case Color.White:
      default:
        return '#fff';
    }
  }

  renderText(text: string, size: FontSize, color: Color, position: Vector2d, ctx: CanvasRenderingContext2D): void {
    const { bufferCanvas, bufferCtx, sprite } = this;

    if (!sprite) {
      return;
    }

    const { x, y } = position;
    ctx.save();
    ctx.translate(TILE_SIZE * x, TILE_SIZE * y);

    const { width, height } = this.getDimensions(size);

    const chars = text.split('');
    const bufferWidth = chars.length * width;

    this.bufferCanvas.width = bufferWidth;
    this.bufferCanvas.height = height;

    bufferCtx.save();
    bufferCtx.clearRect(0, 0, bufferWidth, height);

    chars.forEach((char, index) => this.renderCharacter(char, size, index, bufferCtx));

    bufferCtx.fillStyle = this.getColor(color);
    bufferCtx.globalCompositeOperation = "source-in";
    bufferCtx.fillRect(0, 0, bufferWidth, height);

    bufferCtx.restore();

    ctx.drawImage(bufferCanvas, 0, 0);

    ctx.restore();
  }
}